import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { IonSpinner } from "@ionic/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { getIdTokenResult } from "firebase/auth";
import { getFirestore, doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth } from "../../lib/firebase/firebase";
import { useAuth } from "../../contexts/AuthContext";
import RevenueCatService from "../../lib/revenuecat/RevenueCatService";
import branding from "../../config/branding";
import { isSvgTrace } from "../../config/appVariant";
import { trackInitiateCheckout, trackPurchase, trackViewContent, newEventId } from "../../lib/analytics/metaPixel";
import ProWelcomeScreen from "../ProWelcomeScreen";
import "./IOSPaywall.css";

const CLAIM_POLL_INTERVAL_MS = 2000;
const CLAIM_POLL_MAX_ATTEMPTS = 8;

const BENEFITS = [
  "Full Access to SVG Editor.",
  "Full Access to Embroidery Editor.",
  "Unlimited AI Enhanced Converts.",
  "Commercial Use Image Generator.",
  "Bulk SVG Conversion.",
  "AI Image Resizer.",
];


const IOSPaywall = ({ onClose, onSuccess, action, standalone }) => {
  const { user, refreshPremiumStatus } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState("");
  const [waitingForClaims, setWaitingForClaims] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [introEligible, setIntroEligible] = useState(false);
  const successTimerRef = useRef(null);
  const pendingPkgTypeRef = useRef(null);
  const pendingEventIdRef = useRef(null);

  const subtitle = "Start your free trial today. Cancel anytime during the trial period.";

  useEffect(() => {
    trackViewContent(action || "paywall");
  }, [action]);

  useEffect(() => {
    return () => {
      if (successTimerRef.current) clearTimeout(successTimerRef.current);
    };
  }, []);

  const loadOfferings = useCallback(async () => {
    try {
      console.log("[IOSPaywall] Loading offerings... user:", user?.uid);
      setLoading(true);
      setError("");
      const rc = RevenueCatService.getInstance();

      if (!rc.isInitialized && user?.uid) {
        console.log("[IOSPaywall] Initializing RC...");
        await rc.initialize(user.uid);
      }

      if (!rc.isInitialized) {
        console.error("[IOSPaywall] RC not initialized after init attempt");
        setError("Subscription service not available. Please restart the app.");
        return;
      }

      console.log("[IOSPaywall] Refreshing offerings...");
      await rc.refreshOfferings();
      const pkgs = rc.getPackages();
      console.log("[IOSPaywall] Packages loaded:", pkgs?.length);
      setPackages(pkgs);

      if (pkgs.length > 0) {
        const yearly = pkgs.find(
          (p) =>
            p.packageType === "ANNUAL" ||
            p.identifier === "$rc_annual" ||
            p.product?.identifier?.includes("yearly")
        );
        setSelectedPkg(yearly || pkgs[0]);

        const productIds = pkgs.map((p) => p.product?.identifier).filter(Boolean);
        if (productIds.length > 0) {
          try {
            const eligibility = await rc.checkIntroEligibility(productIds);
            const anyEligible = productIds.some(
              (id) => eligibility[id]?.status === 2
            );
            setIntroEligible(anyEligible);
          } catch (eligErr) {
            setIntroEligible(false);
          }
        }
      }
    } catch (err) {
      console.error("[IOSPaywall] Error loading offerings:", err);
      console.error("[IOSPaywall] Error details:", JSON.stringify(err, Object.getOwnPropertyNames(err)));
      setError(`Unable to load plans: ${err.message || "Unknown error"}`);
    } finally {
      console.log("[IOSPaywall] Loading complete");
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    loadOfferings();
  }, [loadOfferings]);

  const waitForPremiumClaims = async () => {
    setWaitingForClaims(true);
    for (let i = 0; i < CLAIM_POLL_MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, CLAIM_POLL_INTERVAL_MS));
      try {
        if (!auth.currentUser) break;
        const tokenResult = await Promise.race([
          getIdTokenResult(auth.currentUser, true),
          new Promise((_, reject) => setTimeout(() => reject(new Error("Token refresh timeout")), 5000)),
        ]);
        const { stripeRole, lifetimePro } = tokenResult.claims;
        const hasPremium = !!(lifetimePro || stripeRole === "premium" || stripeRole === "designer-subscriber");
        console.log(`[IOSPaywall] Claim poll ${i + 1}/${CLAIM_POLL_MAX_ATTEMPTS}: stripeRole=${stripeRole}, lifetimePro=${lifetimePro}`);
        if (hasPremium) {
          setWaitingForClaims(false);
          return true;
        }
      } catch (err) {
        console.warn(`[IOSPaywall] Claim poll ${i + 1} error:`, err.message);
      }
    }
    console.warn("[IOSPaywall] Claims not ready after polling — proceeding anyway");
    setWaitingForClaims(false);
    return false;
  };

  const activateProAndShowSuccess = async () => {
    trackPurchase(pendingPkgTypeRef.current, pendingEventIdRef.current);
    pendingPkgTypeRef.current = null;
    pendingEventIdRef.current = null;
    setPurchaseSuccess(true);
    setPurchasing(false);
    setRestoring(false);

    // Refresh premium status in AuthContext so the whole app updates immediately
    if (refreshPremiumStatus) {
      try {
        await refreshPremiumStatus();
        console.log("[IOSPaywall] Premium status refreshed in AuthContext");
      } catch (err) {
        console.warn("[IOSPaywall] refreshPremiumStatus error:", err);
      }
    }

    // Also poll claims in background (non-blocking)
    waitForPremiumClaims().then((ready) => {
      if (ready && refreshPremiumStatus) refreshPremiumStatus();
    });
  };

  const handleSuccessDismiss = () => {
    if (onSuccess) onSuccess();
    else onClose();
  };

  const handlePurchase = async () => {
    if (!selectedPkg) return;
    setPurchasing(true);
    setError("");

    const pkgType = selectedPkg?.packageType === "ANNUAL" ? "designerYearly" : "designer";
    pendingPkgTypeRef.current = pkgType;
    trackInitiateCheckout(pkgType);

    // Generate a shared event_id and persist it to Firestore so the RevenueCat
    // webhook (server) can read the same value and use it when firing the CAPI
    // Purchase event. Meta then deduplicates the iOS browser pixel vs CAPI pair.
    const purchaseEventId = `purchase_${newEventId("ios")}`;
    pendingEventIdRef.current = purchaseEventId;
    if (user?.uid) {
      try {
        const db = getFirestore();
        await setDoc(
          doc(db, "users", user.uid),
          {
            pendingIOSPurchaseEventId: purchaseEventId,
            pendingIOSPurchaseEventIdSetAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (persistErr) {
        console.warn("[IOSPaywall] Failed to persist pending event id:", persistErr?.message);
      }
    }

    try {
      const rc = RevenueCatService.getInstance();
      const result = await rc.purchasePackage(selectedPkg);

      if (result.cancelled) {
        setPurchasing(false);
        return;
      }

      if (!result.success) {
        setError(result.error || "Purchase failed. Please try again.");
        setPurchasing(false);
        return;
      }

      console.log("[IOSPaywall] Purchase succeeded, showing success screen...");
      await activateProAndShowSuccess();
    } catch (err) {
      console.error("[IOSPaywall] Purchase error:", err);
      setError("Something went wrong. Please try again.");
      setPurchasing(false);
    }
  };

  const handleRestore = async () => {
    setRestoring(true);
    setError("");

    try {
      const rc = RevenueCatService.getInstance();
      const result = await rc.restorePurchases();

      if (result.isPremium) {
        console.log("[IOSPaywall] Restore succeeded, showing success screen...");
        await activateProAndShowSuccess();
      } else {
        setError("No previous purchases found.");
        setRestoring(false);
      }
    } catch (err) {
      console.error("[IOSPaywall] Restore error:", err);
      setError("Failed to restore purchases. Please try again.");
      setRestoring(false);
    }
  };

  const formatPrice = (pkg) => {
    const product = pkg.product;
    if (product?.priceString) return product.priceString;
    if (product?.price != null) return `$${product.price.toFixed(2)}`;
    return "";
  };

  const getPeriodLabel = (pkg) => {
    const type = pkg.packageType;
    if (type === "ANNUAL" || pkg.identifier === "$rc_annual") return "year";
    if (type === "MONTHLY" || pkg.identifier === "$rc_monthly") return "month";
    return "";
  };

  const getDisplayName = (pkg) => {
    const type = pkg.packageType;
    if (type === "ANNUAL" || pkg.identifier === "$rc_annual") return "Yearly";
    if (type === "MONTHLY" || pkg.identifier === "$rc_monthly") return "Monthly";
    return pkg.product?.title || pkg.identifier || "Plan";
  };

  const hasFreeTrial = introEligible;
  const isBusy = purchasing || restoring || waitingForClaims;


  if (purchaseSuccess) {
    return (
      <ProWelcomeScreen isVisible={true} onDismiss={handleSuccessDismiss} />
    );
  }

  const paywallContent = loading ? (
    <div className="ios-paywall ios-paywall--loading">
      <div className="ios-paywall__loader">
        <IonSpinner name="crescent" style={{ width: "40px", height: "40px", color: "#5E5CE6" }} />
        <p>Loading plans...</p>
      </div>
    </div>
  ) : (
    <div className="ios-paywall">
      <div className="ios-paywall__content">
        <button className="ios-paywall__close" onClick={onClose} disabled={isBusy}>
          <FontAwesomeIcon icon={faTimes} />
        </button>

        <div className="ios-paywall__header">
          <h2 className="ios-paywall__title">
            Try {branding.proName} Free for 7 Days
          </h2>
          <p className="ios-paywall__subtitle">{subtitle}</p>
        </div>

        {error && <div className="ios-paywall__error">{error}</div>}

        <div className="ios-paywall__benefits">
          {BENEFITS.map((text) => (
            <div className="ios-paywall__benefit" key={text}>
              <span className="ios-paywall__benefit-check">✓</span>
              <span className="ios-paywall__benefit-text">{text}</span>
            </div>
          ))}
        </div>

        <div className="ios-paywall__plans">
          {packages.length === 0 && !loading && (
            <div className="ios-paywall__simulator-notice">
              <p>
                Unable to load subscription plans.
              </p>
              <button
                className="ios-paywall__retry-btn"
                onClick={loadOfferings}
                disabled={isBusy}
              >
                Tap to Retry
              </button>
            </div>
          )}
          {(() => {
            const sorted = [...packages].sort((a, b) => {
              const aYearly = a.packageType === "ANNUAL" || a.identifier === "$rc_annual";
              const bYearly = b.packageType === "ANNUAL" || b.identifier === "$rc_annual";
              if (aYearly && !bYearly) return -1;
              if (!aYearly && bYearly) return 1;
              return 0;
            });

            const monthlyPkg = sorted.find(
              (p) => p.packageType === "MONTHLY" || p.identifier === "$rc_monthly"
            );
            const monthlyPrice = monthlyPkg?.product?.price;

            return sorted.map((pkg) => {
              const isYearly =
                pkg.packageType === "ANNUAL" || pkg.identifier === "$rc_annual";
              const isSelected = selectedPkg?.identifier === pkg.identifier;
              const yearlyTotal = pkg.product?.price;

              let savingsLabel = null;
              let perMonth = null;
              if (isYearly && yearlyTotal && monthlyPrice) {
                const monthlyAnnualized = monthlyPrice * 12;
                const saved = Math.round(monthlyAnnualized - yearlyTotal);
                savingsLabel = `Best Value - Save $${saved}`;
                perMonth = `(${formatPrice(pkg).replace(/[^\d.$]/g, '')}/${getPeriodLabel(pkg)} ≈ $${(yearlyTotal / 12).toFixed(2)}/month)`;
              }

              return (
                <div
                  key={pkg.identifier}
                  className={`ios-paywall__plan ${isSelected ? "ios-paywall__plan--selected" : ""}`}
                  onClick={() => !isBusy && setSelectedPkg(pkg)}
                >
                  <div className="ios-paywall__plan-left">
                    <div className="ios-paywall__plan-radio">
                      <div className="ios-paywall__plan-radio-dot" />
                    </div>
                    <span className="ios-paywall__plan-name">{getDisplayName(pkg)}</span>
                    {isYearly && savingsLabel && (
                      <span className="ios-paywall__plan-savings">{savingsLabel}</span>
                    )}
                  </div>
                  <div className="ios-paywall__plan-right">
                    <span className="ios-paywall__plan-price">{formatPrice(pkg)}</span>
                    <span className="ios-paywall__plan-permonth">
                      {isYearly
                        ? `(averages $${yearlyTotal ? (yearlyTotal / 12).toFixed(2) : isSvgTrace ? "7.99" : "9.99"}/month)`
                        : "per month"}
                    </span>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        <button
          className="ios-paywall__cta"
          onClick={handlePurchase}
          disabled={isBusy || !selectedPkg}
        >
          {purchasing || waitingForClaims ? (
            <IonSpinner name="lines" />
          ) : (
            <span>{hasFreeTrial ? "Try for Free" : "Subscribe Now"}</span>
          )}
        </button>

        <p className="ios-paywall__trial-note">
          No charge for 7 days. Cancel anytime during your trial.
        </p>

        <button
          className="ios-paywall__restore"
          onClick={handleRestore}
          disabled={isBusy}
        >
          {restoring ? (
            <IonSpinner name="lines" style={{ color: "#8e8e93", width: "16px", height: "16px" }} />
          ) : (
            "Restore Purchases"
          )}
        </button>

        <div className="ios-paywall__legal">
          <a
            href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
            target="_blank"
            rel="noopener noreferrer"
            className="ios-paywall__legal-link"
          >
            Terms of Use (EULA)
          </a>
          <span className="ios-paywall__legal-separator">|</span>
          <a
            href="https://svgtrace.com/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="ios-paywall__legal-link"
          >
            Privacy Policy
          </a>
        </div>

      </div>
    </div>
  );

  if (standalone) {
    console.log("IOSPaywall: Rendering standalone portal to body");
    return createPortal(paywallContent, document.body);
  }
  console.log("IOSPaywall: Rendering inline");
  return paywallContent;
};

export default IOSPaywall;
